import React, { FunctionComponent } from 'react';
import { Grid } from '@rbx/ui';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import { useTranslation } from '@rbx/intl';
import StandardMarkdown from '@modules/ui/markdown/components/StandardMarkdown';
import getRateLimitMarkdown from '../utils/getRateLimitMarkdown';
import getScopeMarkdown from '../utils/getScopeMarkdown';
import getImmutableMarkdown from '../utils/getImmutableMarkdown';
import getOneOfMarkdown from '../utils/getOneOfMarkdown';
import { useOpenAPIDocument } from '../hooks/useOpenAPIDocument';
import DetailsSwaggerUI from './DetailsSwaggerUI';
import { TryItOutContainer } from './tryItOut';
import useOperationDetailsStyles from './OperationDetails.styles';

type OperationDetailsProps = {
  operation: OpenAPI.OperationObject;
  method: OpenAPI.HttpMethod;
  path: string;
};

const OperationDetails: FunctionComponent<OperationDetailsProps> = ({
  operation,
  method,
  path,
}) => {
  const { translate } = useTranslation();
  const { classes } = useOperationDetailsStyles();
  const document = useOpenAPIDocument();

  const scopes = operation['x-roblox-scopes'] || [];
  const rateLimits = operation['x-roblox-rate-limits'];
  const immutableMarkdown = getImmutableMarkdown(translate, operation, document);
  const oneOfMarkdown = getOneOfMarkdown(translate, operation, document);

  return (
    <TryItOutContainer>
      <Grid container direction='column' spacing={1} classes={{ root: classes.root }}>
        {operation.description && (
          <Grid item>
            <StandardMarkdown linkProps={{ target: '_blank' }}>
              {operation.description}
            </StandardMarkdown>
          </Grid>
        )}

        {scopes.length > 0 && (
          <Grid item>
            <StandardMarkdown linkProps={{ target: '_blank' }}>
              {`#### ${translate('Label.Scopes')}\n${getScopeMarkdown(scopes)}`}
            </StandardMarkdown>
          </Grid>
        )}

        {rateLimits && (
          <Grid item>
            <StandardMarkdown linkProps={{ target: '_blank' }}>
              {`#### ${translate('Label.Limits')}\n${getRateLimitMarkdown(translate, rateLimits)}`}
            </StandardMarkdown>
          </Grid>
        )}

        {immutableMarkdown && (
          <Grid item>
            <StandardMarkdown linkProps={{ target: '_blank' }}>
              {`#### ${translate('Label.Immutable')}\n${immutableMarkdown}`}
            </StandardMarkdown>
          </Grid>
        )}

        {oneOfMarkdown && (
          <Grid item>
            <StandardMarkdown linkProps={{ target: '_blank' }}>
              {`#### ${translate('Label.OneOf')}\n${oneOfMarkdown}`}
            </StandardMarkdown>
          </Grid>
        )}
        <Grid item container direction='row'>
          <Grid item style={{ width: '100%' }}>
            <DetailsSwaggerUI operation={operation} path={path} method={method} />
          </Grid>
        </Grid>
      </Grid>
    </TryItOutContainer>
  );
};

export default OperationDetails;
